import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { useI18n } from "../contexts/I18nContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Phone, Mail, Globe, MapPin } from "lucide-react";

function WorkshopsRecommendation({ city }) {
  const [workshops, setWorkshops] = useState([]);
  useEffect(() => {
    api.get("/workshops", { params: { city } }).then((r) => setWorkshops(r.data.slice(0, 3))).catch(() => {});
  }, [city]);
  if (!workshops.length) return null;
  return (
    <div className="mt-20 border-t border-neutral-200 pt-16" data-testid="workshops-recommendation">
      <div className="overline text-neutral-500 mb-4">Siguiente paso · Opcional</div>
      <h2 className="font-serif text-3xl lg:text-4xl font-light tracking-tight mb-3">Da el acabado final a tu obra</h2>
      <p className="text-sm text-neutral-600 max-w-xl mb-8">
        Trabajamos con talleres de enmarcación de confianza{city ? ` en ${city}` : ""}. Contacta directamente con ellos para una enmarcación profesional a medida.
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workshops.map((w) => (
          <div key={w.id} className="bg-white border border-neutral-200" data-testid={`recommend-workshop-${w.id}`}>
            {w.picture && (
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                <img src={fileUrl(w.picture)} alt={w.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <h3 className="font-serif text-xl">{w.name}</h3>
              <div className="flex items-start gap-2 text-xs text-neutral-600 mt-3">
                <MapPin size={12} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <span>{w.address}, {w.city} {w.postal_code}</span>
              </div>
              {w.description && <p className="text-xs text-neutral-600 mt-3 line-clamp-3">{w.description}</p>}
              <div className="mt-4 space-y-2 text-xs">
                {w.phone && (
                  <a href={`tel:${w.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-neutral-900 hover:underline">
                    <Phone size={12} strokeWidth={1.5} /> {w.phone}
                  </a>
                )}
                {w.email && (
                  <a href={`mailto:${w.email}`} className="flex items-center gap-2 text-neutral-900 hover:underline truncate">
                    <Mail size={12} strokeWidth={1.5} /> {w.email}
                  </a>
                )}
                {w.website && (
                  <a href={w.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-900 hover:underline truncate">
                    <Globe size={12} strokeWidth={1.5} /> {w.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CheckoutPage() {
  const { t } = useI18n();
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_email: user?.email || "",
    shipping_phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_country: "España",
  });

  if (confirmation) {
    return (
      <MarketplaceLayout>
        <section className="max-w-4xl mx-auto px-6 py-24" data-testid="checkout-success">
          <div className="text-center">
            <CheckCircle2 size={48} strokeWidth={1} className="mx-auto text-black" />
            <h1 className="font-serif text-5xl mt-8 font-light">{t.checkout.success}</h1>
            <p className="mt-6 text-neutral-600 max-w-lg mx-auto">{t.checkout.successText}</p>
            <div className="mt-6 overline text-neutral-400">N° {confirmation.id.slice(0, 8).toUpperCase()}</div>
            <Link to="/" className="mt-10 inline-block bg-black text-white px-8 py-4 overline hover:bg-neutral-800 transition-colors">
              {t.cart.continue}
            </Link>
          </div>
          <WorkshopsRecommendation city={confirmation.shipping_city} />
        </section>
      </MarketplaceLayout>
    );
  }

  if (items.length === 0) {
    return (
      <MarketplaceLayout>
        <section className="max-w-xl mx-auto px-6 py-24 text-center">
          <p className="overline text-neutral-400">{t.cart.empty}</p>
          <Link to="/" className="mt-8 inline-block bg-black text-white px-8 py-4 overline hover:bg-neutral-800 transition-colors">
            {t.cart.continue}
          </Link>
        </section>
      </MarketplaceLayout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          artwork_id: i.artwork_id,
          title: i.title,
          image_path: i.image_path,
          artist_id: i.artist_id,
          artist_name: i.artist_name,
          price: i.price,
          production_cost: i.production_cost || 0,
          logistics_cost: i.logistics_cost || 0,
          quantity: i.quantity,
          size_label: i.size_label,
          width_cm: i.width_cm,
          height_cm: i.height_cm,
          material: i.material,
          framing_code: i.framing_code,
          framing_finish: i.framing_finish,
        })),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      setConfirmation(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-neutral-200 px-4 py-3 outline-none focus:border-black bg-white text-sm";

  return (
    <MarketplaceLayout>
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="checkout-page">
        <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tight mb-12">{t.checkout.title}</h1>
        <div className="grid lg:grid-cols-12 gap-12">
          <form onSubmit={submit} className="lg:col-span-7 space-y-4">
            <input required placeholder={t.checkout.name} value={form.shipping_name} onChange={(e) => setForm({ ...form, shipping_name: e.target.value })} className={inputCls} data-testid="checkout-name" />
            <input required type="email" placeholder={t.checkout.email} value={form.shipping_email} onChange={(e) => setForm({ ...form, shipping_email: e.target.value })} className={inputCls} data-testid="checkout-email" />
            <input required placeholder={t.checkout.phone} value={form.shipping_phone} onChange={(e) => setForm({ ...form, shipping_phone: e.target.value })} className={inputCls} data-testid="checkout-phone" />
            <input required placeholder={t.checkout.address} value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className={inputCls} data-testid="checkout-address" />
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder={t.checkout.city} value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} className={inputCls} data-testid="checkout-city" />
              <input required placeholder={t.checkout.country} value={form.shipping_country} onChange={(e) => setForm({ ...form, shipping_country: e.target.value })} className={inputCls} data-testid="checkout-country" />
            </div>
            <button disabled={loading} type="submit" className="bg-black text-white px-10 py-4 overline hover:bg-neutral-800 transition-colors disabled:opacity-50" data-testid="place-order-btn">
              {loading ? "..." : t.checkout.place}
            </button>
          </form>
          <div className="lg:col-span-5">
            <div className="border border-neutral-200 p-8">
              <div className="overline text-neutral-500 mb-6">{t.cart.title}</div>
              <div className="space-y-4 mb-6">
                {items.map((it) => (
                  <div key={it.key} className="flex justify-between text-sm" data-testid={`summary-item-${it.key}`}>
                    <div>
                      <div className="font-serif text-base">{it.title}</div>
                      <div className="text-xs text-neutral-500">{it.size_label ? `${it.size_label} · ` : ""}× {it.quantity}</div>
                      {it.material_name && <div className="text-xs text-neutral-400">{it.material_name}</div>}
                      {it.framing_label && <div className="text-xs text-neutral-900" data-testid={`summary-framing-${it.key}`}>{it.framing_label}</div>}
                    </div>
                    <div>€{(it.price * it.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-neutral-200 pt-4 flex justify-between">
                <span className="overline">{t.cart.subtotal}</span>
                <span className="font-serif text-2xl">€{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketplaceLayout>
  );
}
